import { booleanAttribute, ChangeDetectionStrategy, Component, computed, DestroyRef, effect, ElementRef, inject, Injector, input, model, output, signal, TemplateRef, untracked, viewChild, viewChildren } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { distinctUntilChanged, finalize, interval, of, skipUntil, skipWhile, Subject, switchMap, take, takeWhile, tap, timer } from 'rxjs';
import { AppContext } from 'src/app/app/context';
import { DashboardLayout } from 'src/app/dashboards/models';
import { DashboardDto, DashboardService, DashboardVisualDto, LocaleDto, VisualService } from 'src/app/services/api.services';
import { ContentLoadState } from 'src/app/shared/components/content-placeholder/content-placeholder.component';
import { catchAppError, group, isNullOrUndefined } from 'src/app/shared/utils';
import { DataModelResolver } from '../dataModel/DataModelResolver';
import { debounced } from '../utils/debounced';
import { logger } from '../utils/logger';
import { VisualFieldTargetResolver } from '../visuals/VisualFieldTargetResolver';
import { VisualFilter } from '../visuals/VisualFilter';
import { VisualHost } from '../visuals/VisualHost';
import { IVisualInteractionSource, VisualInteractions } from '../visuals/VisualInteractions';
import { VisualEmbedComponent } from './VisualEmbed.component';
import { DataSchema } from '../dataModel';

const LOGGER = logger('dashboard-embed');

@Component({
  selector: 'dashboard-embed',
  templateUrl: './DashboardEmbed.component.html',
  styleUrl: './DashboardEmbed.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  host: {
    '[class.print]': 'print()'
  }
})
export class DashboardEmbedComponent {
  readonly appContext = inject(AppContext);
  readonly destroyRef = inject(DestroyRef);
  readonly injector = inject(Injector);

  readonly dashboardService = inject(DashboardService);
  readonly visualService = inject(VisualService);
  readonly dataModelResolver = inject(DataModelResolver);

  readonly dataModel = toSignal(this.dataModelResolver.getOrganisationModel());
  readonly showUnshared = input(false);

  readonly localeInput = input<string | LocaleDto>(null, { alias: 'locale' });
  readonly locale = computed<LocaleDto>(() => {
    let locale = this.localeInput();
    if (typeof locale === 'string')
      locale = this.appContext.findLocaleById(locale);

    return locale ?? this.appContext.locale();
  });

  readonly pageHeader = input<TemplateRef<any>>();
  readonly pageFooter = input<TemplateRef<any>>();

  readonly print = input<boolean>(false);
  readonly container = viewChild<ElementRef<HTMLDivElement>>('container');

  readonly busy = input<boolean>(false);

  readonly waitForAllVisuals = input<boolean>(false);

  readonly ContentLoadState = ContentLoadState;
  readonly contentState = signal<ContentLoadState>(ContentLoadState.Loading);
  readonly contentStateOutput = output<ContentLoadState>({ alias: 'contentState' });
  readonly contentStateChangedAt = model<Date>(new Date());

  private setContentState(contentState: ContentLoadState) {
    untracked(() => {
      if (contentState == this.contentState())
        return;

      LOGGER.debug(`ContentState = ${contentState}`, { embed: this, dashboard: this.dashboardDto()?.name });

      this.contentState.set(contentState);
      this.contentStateChangedAt.set(new Date());
      this.contentStateOutput.emit(contentState);

      if ([ContentLoadState.Deleted, ContentLoadState.Error, ContentLoadState.Rendered, ContentLoadState.Unavailable].includes(contentState)) {
        this.emitDashboardRenderedDebounced();
      }
    });
  }

  readonly userDatasetCount = signal(0);
  readonly loadedUserDatasetCount = signal(0);

  private updateStateFromVisuals() {
    let visuals = this.visuals();
    if (!visuals.length) {
      this.setContentState(ContentLoadState.Loaded);
      return;
    }

    if (this.waitForAllVisuals()) {
      if (visuals.some(v => v.contentState() == ContentLoadState.Loading)) {
        this.setContentState(ContentLoadState.Loading);
        return;
      }


      let refreshingVisuals = visuals.filter(v => v.contentState() == ContentLoadState.Refreshing);

      if (this.userDatasetCount() == null)
        this.userDatasetCount.set(refreshingVisuals.length);

      this.loadedUserDatasetCount.set(this.userDatasetCount() - refreshingVisuals.length);

      if (refreshingVisuals.length) {
        this.setContentState(ContentLoadState.Refreshing);
        return;
      }
    }

    let anyVisualNotRendered = visuals.some(x => !x.rendered());
    let allVisualRendered = !anyVisualNotRendered;

    if (allVisualRendered) {
      this.setContentState(ContentLoadState.Rendered);
    }
    else {
      this.setContentState(ContentLoadState.Loaded);
    }
  }

  readonly dashboardRendered = output<void>();

  readonly emitDashboardRenderedDebounced = debounced(
    () => this.dashboardRendered.emit(),
    400,
    this.destroyRef);

  readonly loading = computed(() => [
    ContentLoadState.Loading,
    ContentLoadState.Refreshing
  ].includes(this.contentState()));

  readonly dashboardDtoInput = input<DashboardDto>(null, { alias: 'dashboardDto' });
  readonly dashboardIdInput = input<number>(null, { alias: 'dashboardId' });

  readonly dashboardDto = signal<DashboardDto>(null);
  readonly dashboardId = computed<number>(() => this.dashboardDto()?.id ?? this.dashboardIdInput());

  readonly visualInteractionsInput = input<VisualInteractions>(null, { alias: 'visualInteractions' });
  readonly visualInteractions = computed(() => this.visualInteractionsInput() ?? this.ownVisualInteractions);

  readonly visualHosts = computed(() => this.visualEmbeds()?.map(x => x.visualHost()));
  private readonly ownVisualInteractions = new VisualInteractions(this.injector, this.visualHosts);
  private readonly ownVisualInteractions_UpdateRequestFilters = effect(() => {
    let dataModel = this.dataModel();
    if (!dataModel)
      return;

    if (this.visualInteractionsInput())
      return;

    let fieldResolver = untracked(() => new VisualFieldTargetResolver(dataModel, []));

    let filters = (this.dashboardDto()?.filters ?? []).map(
      x => new VisualFilter({
        predicate: VisualFilter.Predicate.fromFilterDto(x, fieldResolver.find)
      })
    );

    untracked(() => this.ownVisualInteractions.requestFilters.set(filters));
  });

  readonly visuals = signal<ReadonlyArray<DashboardEmbedVisual>>([]);
  readonly visualsOutput = output<ReadonlyArray<DashboardEmbedVisual>>({ alias: 'visuals' });

  readonly visualEmbeds = viewChildren(VisualEmbedComponent);

  private setVisuals(visuals: ReadonlyArray<DashboardEmbedVisual>) {
    this.visuals.set(visuals);
    this.visualsOutput.emit(visuals);
  }

  onVisualRendered(v: DashboardEmbedVisual) {
    v.renderedEventReceived.set(true);
    this.updateStateFromVisuals();
  }


  readonly layout = computed<DashboardLayout>(() => {
    let layout = this.dashboardDto()?.content.layout;
    if (!layout)
      return null;

    return JSON.parse(layout);
  });

  readonly layoutRows = computed(() => {
    let layout = this.layout();
    return layout?.pageHeight ?? layout?.rows ?? 1;
  });

  readonly layoutGap = computed(() => {
    let layout = this.layout();
    return `${layout?.gap ?? 10}px`;
  });

  private containerResizeObserver: ResizeObserver;

  readonly updateContainerResizeObserver = effect(onCleanup => {
    if (!this.containerResizeObserver) {
      this.containerResizeObserver = new ResizeObserver(() => this.onContainerResized());
      onCleanup(() => this.containerResizeObserver?.disconnect());
    }

    this.containerResizeObserver.disconnect();

    if (this.container())
      this.containerResizeObserver.observe(this.container().nativeElement, { box: 'border-box' });
  });

  readonly containerHeight = signal<number>(null);

  private onContainerResized() {
    this.containerHeight.set(Math.floor(this.container()?.nativeElement.getBoundingClientRect().height ?? 0));
  }

  readonly minPageHeight = input<number | string>(400);
  readonly minPageHeightCss = computed(() => {
    let h = this.minPageHeight();
    return typeof h === 'number' ? `${h}px` : h;
  });

  readonly pageHeightInput = input<number | string>('auto', { alias: 'pageHeight' });
  readonly pageHeight = computed(() => {
    let min = this.minPageHeight();
    let h = this.pageHeightInput();
    if (h === 'auto')
      h = this.containerHeight() ?? Math.floor(this.container()?.nativeElement.getBoundingClientRect().height ?? 0);

    if (typeof h === 'number' && isNaN(h))
      h = min;

    return h;
  });

  readonly pageHeightCss = computed(() => {
    let h = this.pageHeight();
    return typeof h === 'number' ? `${h}px` : h;
  });
  
  readonly pageHeaderHeight = input(null);
  readonly pageFooterHeight = input(null);

  readonly pageCount = computed(() => {
    let layout = this.layout();
    if (!layout)
      return 1;

    let maxRow = layout.visuals.reduce((r, v) => Math.max(r, v.y + v.sizeY), 1);
    let pageRows = layout.pageHeight ?? layout.rows;
    return Math.ceil(maxRow / pageRows);
  });

  readonly pages = computed<ReadonlyArray<DashboardPageModel>>(() => {
    let layout = this.layout();
    if (!layout)
      return [];

    let pageRows = layout.pageHeight ?? layout.rows;
    let pageCount = this.pageCount();

    let pages = [];
    let usedIndices = new Set<number>();

    for (let i = pageCount - 1; i >= 0; --i) {
      let minY = i * pageRows;
      let maxY = minY + pageRows;

      let pageVisualIndices = layout.visuals
          .map((v, i) => ({ v, i }))
          .filter(({ v, i }) => v.y >= minY && !usedIndices.has(i))
          .map(({ i }) => i);

      pageVisualIndices.forEach(x => usedIndices.add(x));

      pages.push(new DashboardPageModel(
        this,
        i,
        pageVisualIndices,
        minY,
        maxY));
    }

    pages.reverse();

    return pages;
  });


  readonly canRender = computed(() =>
    [ContentLoadState.Loaded, ContentLoadState.Rendered].includes(this.contentState())
    && this.dashboardDto() && !!this.visuals()?.length);
  

  private onDashboardDtoLoaded(dashboardDto: DashboardDto) {
    this.dashboardDto.set(dashboardDto);

    let allHaveOrder = !dashboardDto.content.visuals.some(v => isNullOrUndefined(v.order));

    let dashboardVisuals = dashboardDto.content.visuals.map(
      (v, i) => new DashboardEmbedVisual(
          this, v, i,
          allHaveOrder ? (v.order ?? (999 + i)) : i)
    );

    this.setVisuals(dashboardVisuals);
    this.updateStateFromVisuals();

    return of(null);
  }


  readonly loadDashboard = effect(() => {
    let dashboardDto = this.dashboardDtoInput();
    if (dashboardDto) {
      this.loadDashboardById$.next(0);
      this.loadDashboardDto$.next(dashboardDto);
    }
    else {
      let dashboardId = this.dashboardIdInput();
      if (dashboardId) {
        this.loadDashboardDto$.next(null);
        this.loadDashboardById$.next(dashboardId);
      } else {
        this.setContentState(ContentLoadState.Unavailable);
      }
    }
  });

  readonly loadDashboardDto$ = new Subject<DashboardDto>();
  readonly loadDashboardDto = this.loadDashboardDto$.pipe(
    distinctUntilChanged(),
    switchMap(dashboardDto => {
      if (dashboardDto) {
        this.setVisuals([]);
        this.setContentState(ContentLoadState.Loading);
        
        return this.onDashboardDtoLoaded(dashboardDto);
      }
      else {
        return of(null);
      }
    }),
    catchAppError(() => this.setContentState(ContentLoadState.Error), { showErrorSnack: false }),
    takeUntilDestroyed(this.destroyRef)
  ).subscribe();

  readonly loadDashboardById$ = new Subject<number>();
  readonly loadDashboardById = this.loadDashboardById$.pipe(
    distinctUntilChanged(),
    switchMap(dashboardId => {
      if (dashboardId) {
        this.setVisuals([]);
        this.setContentState(ContentLoadState.Loading);

        return this.dashboardService.getDashboard(dashboardId).pipe(
          switchMap(d => this.onDashboardDtoLoaded(d))
        );
      }
      else {
        return of(null);
      }
    }),
    catchAppError(() => this.setContentState(ContentLoadState.Error), { showErrorSnack: false }),
    takeUntilDestroyed(this.destroyRef)
  ).subscribe();



  readonly checkInputs = effect(() => {
    let dashboardDto = this.dashboardDtoInput();
    let dashboardId = this.dashboardIdInput();

    if ([dashboardDto, dashboardId].filter(x => !!x).length > 1) {
      LOGGER.warn(`Only one of [dashboardDto] or [dashboardId] inputs should be provided, not both.`);
    }
  });

  readonly disableTitle = input(false, { transform: booleanAttribute });
  readonly disableActions = input(false, { transform: booleanAttribute });
  readonly disableInteraction = input(false, { transform: booleanAttribute });

  readonly allowApiDownloadLinkDialog = input(true);
  readonly allowExport = input(true);

  readonly showFocusToggle = input(true);
  readonly focused = model<DashboardEmbedVisual>(null);

  focusVisual(v: DashboardEmbedVisual) {
    this.focused.update(x => x == v ? null : v);
  }

  onClick(e: MouseEvent) {
    if (e.button !== 0)
      return;

    if (this.visualEmbeds()?.some(x => x.actionMenuOpened()))
      return;
      
    let target = e.target as HTMLElement;

    if (!['.embed-visual__content', '.embed-visual__actions'].some(x => target.closest(x))) {
      this.clearVisualSelections();
    }
  }

  clearVisualSelections() {
    untracked(() => {
      try {
       this.visualInteractions()?.crossFilter.source.set(null);
      }
      finally {
        this.visualEmbeds()?.forEach(ve => {
          try {
            ve.clearSelection();
          }
          catch (err) {
            LOGGER.error(err);
          }
        });
      }
    });
  }

  private _setInteractionSource(source: VisualHost) {
    let visualEmbeds = this.visualEmbeds() ?? [];
    visualEmbeds.forEach(ve => {
      let h = ve.visualHost();
      if (h != source)
        h.onBecomeCrossFilterTarget(VisualHost.EventTrigger.External);
    });

    this.visualInteractions()?.crossFilter.source.set(source);
    source?.onBecomeCrossFilterSource(VisualHost.EventTrigger.External);
  }

  private readonly clearInteractionSourceDebounced = debounced(() => {
    this._setInteractionSource(null);
  }, 1, this.destroyRef);


  private readonly setInteractionSourceDebounced = debounced((source: VisualHost) => {
    this._setInteractionSource(source);
  }, 200, this.destroyRef);


  onBeforeVisualEvent(e: VisualHost.Event) {
    if (e.type == VisualHost.EventType.ClickEarlyEvent) {
      if (this.focused())
        return;

      let currentSource = this.visualInteractions()?.crossFilter.source();
      if (currentSource && e.resetCrossFilter) {
        this.clearInteractionSourceDebounced(null);
      }
    }
  }

  onVisualEvent(e: VisualHost.Event) {
    if (e.type == VisualHost.EventType.SelectionChangeEvent) {
      let currentSource = this.visualInteractions()?.crossFilter.source();
      if (currentSource && (currentSource != e.host || !e.dataPoints?.length)) {
        this.clearInteractionSourceDebounced(null);
      }

      if (e.dataPoints?.length) {
        this.setInteractionSourceDebounced(e.host);
      }
    }
  }
}

export class DashboardEmbedVisual {
  constructor(
    readonly component: DashboardEmbedComponent,
    readonly dashboardVisualDto: DashboardVisualDto,
    readonly index: number,
    readonly layoutIndex: number) { }

  readonly embed = computed(() => this.component.visualEmbeds()[this.index]);

  readonly willRender = computed(() => {
    if (this.dashboardVisualDto.deleted)
      return false;

    if (this.dashboardVisualDto.unshared) {
      if (!this.component.showUnshared()) {
        return false;
      }
    }

    return true;
  });

  readonly contentState = computed(() => 
    !this.willRender()
      ? ContentLoadState.Deleted
      : this.embed()?.contentState() ?? ContentLoadState.Loading);

  readonly contentStateChangedAt = computed(() => this.embed()?.contentStateChangedAt());

  readonly renderedEventReceived = signal(false);

  // visual is 'rendered' when it is deleted, cause there is nothing to display
  readonly rendered = computed(() => {
    if (!this.willRender())
      return true;

    if (this.embed()) {
      if (this.embed().visualRendered())
        return true;
    }

    return this.renderedEventReceived();
  });

  readonly visual = computed(() => this.embed()?.visual());

  readonly page = computed(() => this.component.pages().find(p => p.visualLayoutIndices.includes(this.layoutIndex)));

  readonly layout = computed(() => {
    let layout = this.component.layout();
    let visualLayout = this.layoutIndex < layout.visuals.length ? layout.visuals[this.layoutIndex] : null;
    return visualLayout;
  });

  readonly layoutStyle = computed(() => {
    let layout = this.layout();
    if (!layout)
      return '';

    return `
      grid-column-start: ${layout.x + 1};
      grid-column-end: span ${layout.sizeX};
      grid-row: 1;
      height: 0;
    `;
  });

  readonly innerLayoutStyle = computed(() => {
    let layout = this.layout();
    if (!layout)
      return '';

    let page = this.page();
    let pageRows = this.component.layoutRows();
    
    let minY = layout.y - page.minY;
    let t = [];
    let b = [];

    for (let i = 0; i < minY; ++i) {
      pushYOffset(t, i);
    }

    b.push(...t);

    let maxY = minY + layout.sizeY;
    for (let i = minY; i < maxY; ++i) {
      pushYOffset(b, i);
    }
    
    function pushYOffset(list: string[], i: number) {
      if (i > 0)
        list.push('var(--dashboard-page-grid-gap)');

      list.push('var(--dashboard-page-grid-cell-h)');

      let pageBreakpoint = i > 0 && (i % pageRows) == 0;
      if (pageBreakpoint) {
        list.push('var(--dashboard-page-header-height)');
        list.push('var(--dashboard-page-footer-height)');
      }
    }
    
    if (t.length == 0)
      t = ['0px'];

    if ((maxY % pageRows) == 0)
      b.push('var(--dashboard-page-grid-gap)');

    return `
      --dashboard-visual-layout-t: calc(${t.join(' + ')});
      --dashboard-visual-layout-b: calc(${b.join(' + ')});

      top:    var(--dashboard-visual-layout-t);
      height: calc(var(--dashboard-visual-layout-b) - var(--dashboard-visual-layout-t));
    `;
  });
}

export class DashboardPageModel {
  constructor(
    public readonly owner: DashboardEmbedComponent,
    public readonly index: number,
    public readonly visualLayoutIndices: ReadonlyArray<number>,
    public readonly minY: number,
    public readonly maxY: number) { }

  readonly visuals = computed(() => {
    let ownerVisuals = this.owner.visuals();
    if (!ownerVisuals?.length)
      return [];

    return this.visualLayoutIndices.map(i => ownerVisuals.find(x => i == x.layoutIndex));
  });

  readonly layoutGridStyle = computed(() => {
    let layout = this.owner.layout();
    if (!layout)
      return null;

    return `
      grid-template-columns: repeat(${layout.columns}, [col-start] 1fr);
      grid-template-rows: 1fr;
      column-gap: ${layout.gap ?? 10}px;
      `;
  });
}
